/**
 * Replies that arrived carrying no reference.
 *
 * ⚠️ THIS IS THE ONLY PLACE THEY ARE VISIBLE. A carrier who starts a fresh email
 * rather than replying carries nothing to match on, so route-reply stores the
 * message with a null reference — captured, and until now lost in practice. The
 * migration's own comment calls this the triage queue; this is it.
 *
 * ⚠️ STAFF ONLY, AND NOT MERELY OUT OF CAUTION. An unmatched message is by
 * definition one nobody can attribute to an agency. It could concern any
 * member's insured, so showing the queue to members would mean showing one
 * agency another agency's correspondence on the grounds that we could not work
 * out whose it was. The API enforces this; the component simply never renders
 * for anyone else.
 *
 * ⚠️ AND IT HIDES ITSELF WHEN EMPTY. An always-present panel reading "nothing
 * unmatched" on a dashboard people open every morning is furniture. This should
 * only ever appear because something needs doing.
 */
import { useEffect, useState } from 'react';
import { ApiError, api, type RequestMessage } from '../lib/api';

const when = (iso: string): string =>
  new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });

export function UnmatchedQueue({ onFiled }: { onFiled: () => void }): JSX.Element | null {
  const [msgs, setMsgs] = useState<RequestMessage[]>([]);
  const [refs, setRefs] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState('');
  const [err, setErr] = useState('');

  const load = () => {
    api
      .unmatchedMessages()
      .then((r) => setMsgs(r.messages))
      // A 403 is the ordinary answer for a member. Silence, not an error.
      .catch(() => setMsgs([]));
  };

  useEffect(load, []);

  const file = async (id: string, reference?: string) => {
    setBusy(id);
    setErr('');
    try {
      await api.fileUnmatched(id, reference);
      setMsgs((prev) => prev.filter((m) => m.id !== id));
      // The row it was filed against now has a reply, so the list behind this
      // is out of date the moment this succeeds.
      if (reference) onFiled();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Could not file that.');
    } finally {
      setBusy('');
    }
  };

  if (!msgs.length) return null;

  return (
    <section className="panel uq">
      <div className="uq-head">
        <h3>
          Unmatched replies
          <span className="uq-count">{msgs.length}</span>
        </h3>
        <p>
          These arrived without a reference — usually a carrier starting a new email instead of
          replying. Nothing links them to a request until somebody says which one.
        </p>
      </div>

      {err && <p className="is-warn uq-err">{err}</p>}

      <ul className="uq-list">
        {msgs.map((m) => (
          <li key={m.id}>
            <div className="uq-msg">
              <div className="uq-from">
                <b>{m.from_name || m.from_email || 'Unknown sender'}</b>
                <span>{when(m.created_at)}</span>
              </div>
              {m.subject && <div className="uq-subject">{m.subject}</div>}
              {m.body_text && <p className="uq-body">{m.body_text.slice(0, 400)}</p>}
              {m.attachments.length > 0 && (
                <div className="uq-files">
                  {/* Not openable from here: the signed-URL route is scoped to a
                      reference, and this message has none yet. File it first. */}
                  {m.attachments.map((f) => (
                    <span key={f.storagePath} className="uq-file">
                      {f.name}
                    </span>
                  ))}
                </div>
              )}
            </div>

            <div className="uq-act">
              <input
                type="text"
                value={refs[m.id] ?? ''}
                onChange={(e) => setRefs({ ...refs, [m.id]: e.target.value.toUpperCase() })}
                placeholder="LR-4831QA"
                aria-label="Request reference"
                spellCheck={false}
              />
              <button
                className="btn btn-sm btn-primary"
                type="button"
                disabled={busy === m.id || !(refs[m.id] ?? '').trim()}
                onClick={() => void file(m.id, refs[m.id])}
              >
                {busy === m.id ? 'Filing…' : 'File it'}
              </button>
              {/*
                ⚠️ "Not ours" MARKS IT HANDLED AND KEEPS IT. The row stays in the
                table with handled_at set, so a message set aside by mistake can
                still be found. Deleting would make this button the one
                irreversible control on the page.
              */}
              <button
                className="btn btn-sm btn-ghost"
                type="button"
                disabled={busy === m.id}
                onClick={() => void file(m.id)}
              >
                Not ours
              </button>
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}
